import {
  getFileExtension,
  IAudioFile,
  IDocFile,
  IFile,
  IOrientation,
  IPhotoFile,
  IVideoFile,
} from "@home-health-notify/domain";
import { generateVideoThumbnails } from "@rajesh896/video-thumbnails-generator";
import { dataURLtoFile } from "hhn-commons";
import { getAudioDuration } from "../utils/MediaUtils";
import { v4 as uuidv4 } from "uuid";

export interface FileWithSafePath extends File {
  filePath: string;
}

interface MediaFileBase extends FileWithSafePath {
  uniqueName: string;
  dimension?: {
    width: number;
    height: number;
  };

  ext(): string;

  isImage(): boolean;

  isVideo(): boolean;

  isAudio(): boolean;

  isPDF(): boolean;

  isDoc(): boolean;

  setDimension(width: number, height: number): void;

  getOrientation(): IOrientation | undefined;

  getUniqueNameWithDimensions(): string;
}

export const mediaFileAddons = {
  ext: function (this: MediaFileBase) {
    return getFileExtension(this.name);
  },

  isImage: function (this: MediaFileBase) {
    return this.type.startsWith("image/");
  },

  isVideo: function (this: MediaFileBase) {
    return this.type.startsWith("video/");
  },

  isAudio: function (this: MediaFileBase) {
    return this.type.startsWith("audio/");
  },

  isPDF: function (this: MediaFileBase) {
    return this.type === "application/pdf";
  },

  isDoc: function (this: MediaFileBase) {
    return !this.isImage() && !this.isVideo() && !this.isAudio();
  },

  setDimension: function (this: MediaFileBase, width: number, height: number) {
    this.dimension = {
      width,
      height,
    };
  },

  getOrientation: function (this: MediaFileBase): IOrientation | undefined {
    if (this.dimension) {
      return this.dimension.width > this.dimension.height
        ? "LANDSCAPE"
        : "PORTRAIT";
    }
  },

  getUniqueNameWithDimensions: function (this: MediaFileBase): string {
    const [name, ext] = this.uniqueName.split(".");
    if (this.isImage() && this.dimension) {
      const { width, height } = this.dimension;
      return `${name}_w${width}_h${height}.${ext}`;
    } else {
      return `${name}.${ext}`;
    }
  },
};

// Helper function to add both uniqueName and mediaFileAddons to any file
const prepareMediaFile = (
  fileWithPath: FileWithSafePath,
  isThumbnail: boolean = false,
) => {
  if (isThumbnail) {
    return Object.assign(
      fileWithPath,
      { uniqueName: fileWithPath.name },
      mediaFileAddons,
    );
  }

  const ext = getFileExtension(fileWithPath.name);
  const uniqueId = uuidv4();
  const uniqueName = `${uniqueId}.${ext}`;
  return Object.assign(fileWithPath, { uniqueName }, mediaFileAddons);
};

export const toDocFile = (fileWithPath: FileWithSafePath): IDocFile => {
  return Object.assign(prepareMediaFile(fileWithPath), {
    getThumbnail: async () => null as any,
  }) as IDocFile;
};

export const toPhotoFile = (
  fileWithPath: FileWithSafePath,
  isThumbnail: boolean = false,
): IPhotoFile => {
  const photo = prepareMediaFile(fileWithPath, isThumbnail);
  return Object.assign(photo, { getThumbnail: async () => photo });
};

export const toAudioFile = async (
  fileWithPath: FileWithSafePath,
): Promise<IAudioFile> => {
  let file = prepareMediaFile(fileWithPath);
  const duration = await getAudioDuration(fileWithPath);
  return Object.assign(file, {
    duration,
    getThumbnail: async () => null as any,
  }) as IAudioFile;
};

export const toVideoFile = async (
  fileWithPath: FileWithSafePath,
): Promise<IVideoFile> => {
  let file = prepareMediaFile(fileWithPath);
  const duration = await getAudioDuration(fileWithPath);
  const dataURLs = await generateVideoThumbnails(fileWithPath, 1, "Url");

  const [name] = file.uniqueName.split(".");
  const tnFileObj: File = dataURLtoFile(dataURLs[0], `${name}_thumbnail.jpg`);

  const thumbnailFile: IFile = toPhotoFile(
    Object.assign(tnFileObj, { filePath: URL.createObjectURL(tnFileObj) }),
    true,
  );

  return Object.assign(file, {
    duration,
    getThumbnail: async () => thumbnailFile,
  });
};
